import { protectedGet, protectedPost, protectedDeleteWithBody, api } from "./restUtil";
import queryString from "querystring";
import { ASSETS_FOLDER_NAME, EDITOR_FILE_NAME } from "../model/content";
import ContentServiceProvider from "./content/ContentServiceProvider";
import { REDIRECTION_MAPPING } from "./content/redirects";

const DEFAULT_UNSUPPORTED_CONTENT_MESSAGE =
  "Unfortunately we don\u2019t support this content " +
  "type yet but we\u2019ll add it very soon.";

export function getRedirectId(id) {
  if (id in REDIRECTION_MAPPING) {
    return REDIRECTION_MAPPING[id];
  }
  return null;
}

export function browseRoot(args, onSuccess, onError) {
  return protectedGet("/library", undefined, onSuccess, onError);
}

export function browseFeaturedRoot(args, onSuccess, onError) {
  return protectedGet("/library/featured", undefined, onSuccess, onError);
}

export function browseContentUsage(params, onSuccess, onError) {
  return protectedGet("/library/usage", params, onSuccess, onError);
}

export function browseWorkshopRoot() {
  return api.get("/workshops");
}

export function browseInProgressLibraries(args, params, onSuccess, onError) {
  return protectedGet("/library/started", params, onSuccess, onError);
}

export function browseCompletedLibraries(params, onSuccess, onError) {
  return protectedGet("/library/completed", params, onSuccess, onError);
}

export function browseContentBookmarked(params, onSuccess, onError) {
  return protectedGet("/library/favorite", params, onSuccess, onError);
}

export function browseContent(args, onSuccess, onError) {
  return protectedGet("/library/" + args.id, undefined, onSuccess, onError);
}

export function fetchTags(args, onSuccess, onError) {
  return protectedGet("/tags", args, onSuccess, onError);
}

export function searchCourse(args, onSuccess, onError) {
  return protectedGet("/library/course/search", args, onSuccess, onError);
}

export function fetchFileContentBody(args, onSuccess, onError) {
  return protectedGet(
    "/library/" + args.id + "/content",
    undefined,
    resp => {
      if (
        !resp ||
        !resp.type ||
        (resp.type.indexOf("html") === -1 && resp.type.indexOf("xml") === -1)
      ) {
        return onError && onError(DEFAULT_UNSUPPORTED_CONTENT_MESSAGE);
      }
      return onSuccess && onSuccess(resp.xhr.responseXML);
    },
    onError,
    {
      // Thanks to this additional parameter, we will receive the whole response
      // with headers and content type, not only body.
      returnResponse: true,
    }
  );
}

export function addTagToLibraryObject(args, onSuccess, onError) {
  protectedPost(
    "/library/" + args.id + "/tags",
    {
      tags: args.tags,
    },
    onSuccess,
    onError
  );
}

export function deleteTagFromLibraryObject(args, onSuccess, onError) {
  protectedDeleteWithBody(
    "/library/" + args.id + "/tags",
    {
      tags: args.tags,
    },
    onSuccess,
    onError
  );
}

export function isCambridgeBranch(obj) {
  if (!obj || !obj.key) {
    return false;
  }
  return obj.key.toLowerCase().indexOf("cambridge") > -1;
}

export function fetchExercisesBySource(args, onSuccess, onError) {
  return protectedGet(
    "/library/" + args.id + "/exercises",
    {
      page: args.page,
      size: args.size,
    },
    onSuccess,
    onError
  );
}

export function getKeyFromQueryString(location, prefix) {
  if (!location.search) {
    return prefix || "";
  }
  const queryParams = queryString.parse(location.search.substring(1));
  return typeof queryParams.key === "string" ? queryParams.key : queryParams.key[0];
}

export function getS3PrefixFromExerciseKey(s3ExerciseKey) {
  if (!s3ExerciseKey) {
    return "";
  }
  // The key should have the following form:
  // "{s3_prefix}/{exercise_id}/{exercise_id}.xml".
  const keyParts = s3ExerciseKey.split("/");
  if (keyParts.length < 2) {
    // TODO throw an error?
    return "";
  }
  let s3Prefix = "";
  for (let i = 0; i < keyParts.length - 2; i++) {
    s3Prefix += keyParts[i] + "/";
  }
  return s3Prefix;
}

export function getKeyWithoutSlash(key) {
  if (key.endsWith("/")) {
    return key.substring(0, key.length - 1);
  } else {
    return key;
  }
}

export function isFolderEditable(key) {
  const pathParts = key.split("/");
  return pathParts.indexOf(ASSETS_FOLDER_NAME) === -1;
}

export function isFileEditable(key) {
  return isFolderEditable(key) || key.endsWith("xhtml");
}

export function createFolder(newObject, parentKey) {
  return ContentServiceProvider.getContentService().createFolder(newObject, parentKey);
}

export function editFolderMetadata(object) {
  return ContentServiceProvider.getContentService().editFolderMetadata(object);
}

export function renameFolder(object) {
  return ContentServiceProvider.getContentService().renameFolder(object);
}

export function moveFolder(oldKey, newKey) {
  return ContentServiceProvider.getContentService().moveFolder(oldKey, newKey);
}

export function createFile(newObject, parentKey, contentFiles, assetFiles) {
  return ContentServiceProvider.getContentService().createFile(
    newObject,
    parentKey,
    contentFiles,
    assetFiles
  );
}

export function editFile(parentKey, contentFiles, assetFiles) {
  return ContentServiceProvider.getContentService().editFile(
    parentKey,
    contentFiles,
    assetFiles
  );
}

export function getFolderContentWithLibraryObjects(args, onSuccess, onError) {
  // Used by admins, library objects let show if the corresponding folders are saved in our db as LibraryObjects.
  ContentServiceProvider.getContentService()
    .getFolderContent(args.key)
    .then(folderResponse => {
      const setLibraryObjects = libraryObjects => {
        folderResponse.libraryObjects = libraryObjects;
        for (let i = 0; i < folderResponse.folders.length; i++) {
          folderResponse.folders[i].libraryObject =
            libraryObjects[folderResponse.folders[i].key];
        }
        for (let i = 0; i < folderResponse.files.length; i++) {
          folderResponse.files[i].libraryObject =
            libraryObjects[folderResponse.files[i].key];
        }
        onSuccess(folderResponse);
      };
      if (args.libraryObjects) {
        setLibraryObjects(args.libraryObjects);
      } else {
        protectedGet(
          "/library",
          args.key ? "key=" + encodeURIComponent(args.key) : undefined,
          resp => {
            const libraryObjects = {};
            for (let i = 0; i < resp.list.length; i++) {
              libraryObjects[resp.list[i].key] = resp.list[i];
            }
            setLibraryObjects(libraryObjects);
          },
          () => {
            // Ignore?
            onSuccess(folderResponse);
          }
        );
      }
    })
    .catch(onError);
}

export function getFolderContent(args, onSuccess, onError) {
  ContentServiceProvider.getContentService()
    .getFolderContent(args.key, args.maxNumberOfKeys)
    .then(onSuccess)
    .catch(onError);
}

export function removeFolder(args, onSuccess, onError) {
  ContentServiceProvider.getContentService()
    .removeFolder(args.key)
    .then(onSuccess)
    .catch(onError);
}

export function getObjectMetadata(key) {
  return ContentServiceProvider.getContentService().getObjectMetadata(key);
}

export function getObject(key) {
  return ContentServiceProvider.getContentService().getObject(key);
}

export function fetchEditorData(args, onSuccess, onError) {
  getObject(args.parentKey + "/" + EDITOR_FILE_NAME)
    .then(data => {
      onSuccess(JSON.parse(new TextDecoder("utf-8").decode(data.Body)));
    })
    .catch(onError);
}

export function getAssetFileURL(key) {
  return function (fileName) {
    return ContentServiceProvider.getContentNavigator().getResourceURL(key, fileName);
  };
}

export function fetchLibraryVersionAndContentFile(args, onSuccess, onError) {
  const service = ContentServiceProvider.getContentNavigator();
  return Promise.all([
    service.fetchEditorFile(args.libraryVersionId),
    service.getVersion(args.libraryVersionId),
  ])
    .then(([file, version]) => {
      return { file: file, version: version };
    })
    .then(onSuccess)
    .catch(onError);
}

export function fetchLibraryObjectAndContentFile(args, onSuccess, onError) {
  const service = ContentServiceProvider.getContentNavigator();
  return Promise.all([
    service.fetchEditorFile(undefined, args.libraryObjectId),
    service.getObject(args.libraryObjectId),
  ])
    .then(([file, object]) => {
      return { file: file, object: object };
    })
    .then(onSuccess)
    .catch(onError);
}

export function clearContentCredentials() {
  ContentServiceProvider.getContentService().clearCredentials();
}
